const setCalcAddEnabled = (calcAdd, enabled) => {
  if (!calcAdd) return;
  calcAdd.classList.toggle("disabled", !enabled);
  calcAdd.disabled = !enabled;
};

const normalizeFootageCalculatorHref = (href) =>
  String(href || "")
    .replace(/&quot;/gi, "")
    .replace(/"/g, "")
    .trim()
    .toLowerCase();

const isFootageCalculatorHash = (href) => normalizeFootageCalculatorHref(href).startsWith("#footage-calculator");

if (!window.__footageCalculatorHashListenerBound) {
  document.addEventListener("click", (event) => {
    const anchor = event.target.closest("a[href]");
    if (!anchor || !isFootageCalculatorHash(anchor.getAttribute("href"))) return;

    event.preventDefault();

    const openTrigger = document.querySelector('[data-action="open-footage-calculator"], .trigger-footage-calculator');
    if (openTrigger) openTrigger.click();
  });

  window.__footageCalculatorHashListenerBound = true;
}

class FootageCalculator extends HTMLElement {
  connectedCallback() {
    this._walls = [];
    this._init();
  }

  _getPdp() {
    const pdpId = this.dataset?.pdpId;
    return pdpId ? document.getElementById(pdpId) : document.querySelector("section.main-product-pdp");
  }

  _getFromPdp(selector) {
    const pdp = this._getPdp();
    return pdp ? pdp.querySelector(selector) : document.querySelector(selector);
  }

  _init() {
    const inputHeight = this.querySelector("#calc-height");
    const inputWidth = this.querySelector("#calc-width");
    const add15 = this.querySelector("#add15");
    const calcAdd = this.querySelector(".trigger-calc-add");
    const addWallBtn = this.querySelector(".calc-add-wall");

    if (!inputWidth || !inputHeight) return;

    const pdp = this._getPdp();
    const sizeSelect = this._getFromPdp("select[name='options[Size]'], select[name='Size']");
    const sizeRadios = Array.from(
      pdp
        ? pdp.querySelectorAll("input[type='radio'][name='Size'], input[type='radio'][name='options[Size]']")
        : document.querySelectorAll("input[type='radio'][name='Size'], input[type='radio'][name='options[Size]']")
    );

    const setSizeValue = (value) => {
      if (sizeSelect) {
        sizeSelect.value = value;
        sizeSelect.dispatchEvent(new Event("change", { bubbles: true }));
        return true;
      }

      const matchedRadio = sizeRadios.find((radio) => radio.value === value);
      if (matchedRadio) {
        if (!matchedRadio.checked) matchedRadio.click();
        matchedRadio.checked = true;
        matchedRadio.dispatchEvent(new Event("input", { bubbles: true }));
        matchedRadio.dispatchEvent(new Event("change", { bubbles: true }));
        return true;
      }

      return false;
    };

    const changeHandler = () => this._inputChange();
    inputHeight.addEventListener("keyup", changeHandler);
    inputHeight.addEventListener("input", changeHandler);
    inputHeight.addEventListener("change", changeHandler);
    inputWidth.addEventListener("keyup", changeHandler);
    inputWidth.addEventListener("input", changeHandler);
    inputWidth.addEventListener("change", changeHandler);
    add15.addEventListener("change", changeHandler);

    addWallBtn.addEventListener("click", () => this._addWall());

    // --- Material dropdown ---
    const materialSelect = this.querySelector("#calc-material");
    if (materialSelect) {
      // Sync to whatever is currently checked on the PDP (handles a variant
      // being selected before the calculator was opened).
      const checkedMaterialRadio = this._getFromPdp("input[type='radio'][name='Material']:checked");
      if (checkedMaterialRadio) materialSelect.value = checkedMaterialRadio.value;

      // Calculator → PDP: user picks material inside the calculator.
      materialSelect.addEventListener("change", () => {
        const val = materialSelect.value;
        if (!val) return;
        this._setOptionValue("Material", val);
        requestAnimationFrame(() => this._inputChange());
      });
    }

    // --- PDP → Calculator: keep in sync when options change on the main page ---
    // VariantSelects.onVariantChange() runs synchronously (registered in its
    // constructor) so currentVariant is already updated by the time our
    // listener fires.
    const variantPicker = this._getFromPdp("variant-radios, variant-selects");
    if (variantPicker) {
      variantPicker.addEventListener("change", () => {
        // Sync the material dropdown to match whatever was picked on the PDP.
        if (materialSelect) {
          const checkedRadio = this._getFromPdp("input[type='radio'][name='Material']:checked");
          if (checkedRadio && materialSelect.value !== checkedRadio.value) {
            materialSelect.value = checkedRadio.value;
          }
        }
        // Refresh panel counts and pricing now that currentVariant has changed.
        this._inputChange();
      });
    }

    const selections = this.querySelectorAll(".calc-selection");

    // Populate "panels required" column with defaults so it never appears empty.
    selections.forEach((select) => {
      const verbage = select.getAttribute("data-verbage") || "panels";
      const updateElem = select.querySelector(".calc-update");
      if (updateElem && !updateElem.textContent.trim()) {
        updateElem.textContent = `0 ${verbage}`;
      }
    });

    selections.forEach((select) => {
      select.addEventListener("click", () => {
        select.classList.remove("disabled");
        select.classList.add("is-selected");

        selections.forEach((el) => {
          if (el !== select) {
            el.classList.add("disabled");
            el.classList.remove("is-selected");
          }
        });

        const variantPicker = this._getFromPdp("variant-selects, variant-radios");
        const size = select.getAttribute("data-selector").replaceAll("-", " ");
        setSizeValue(size);
        const quantityCount = parseInt(select.getAttribute("data-count"), 10) || 1;
        select.setAttribute("data-count", quantityCount);
        const quantityInput =
          this._getFromPdp(".product-form__quantity .quantity__input") ||
          this._getFromPdp("input[name='quantity']");
        if (quantityInput) quantityInput.value = quantityCount;

        const calcPrice = this.querySelector("#calc-price");
        const price = variantPicker?.currentVariant?.price;
        if (price) {
          const curPrice = price / 100;
          calcPrice.textContent =
            "$" +
            (curPrice * parseInt(select.getAttribute("data-count"))).toLocaleString("en-US", {
              minimumFractionDigits: 0,
            });
          calcPrice.setAttribute("data-usd", curPrice * parseInt(select.getAttribute("data-count")));
          calcAdd.setAttribute("data-quantity", quantityCount);
        } else {
          calcPrice.textContent = "$0";
          calcPrice.setAttribute("data-usd", "0");
        }

        setCalcAddEnabled(calcAdd, true);
      });
    });

    // Setup add to cart
    let lock = false;
    setCalcAddEnabled(calcAdd, false);

    calcAdd.addEventListener("click", () => {
      if (lock) return;
      lock = true;

      this.querySelectorAll(".js-product-footage-close").forEach((trigger) => trigger.click());

      const addToCartButton =
        this._getFromPdp(".js-go-cart-add-to-cart.product-form__submit") ||
        this._getFromPdp("[data-pdp-atc].product-form__submit");
      const productForm = addToCartButton?.closest("form");

      if (productForm && typeof productForm.requestSubmit === "function") {
        productForm.requestSubmit(addToCartButton);
        console.log("productForm.requestSubmit(addToCartButton)");
      } else if (addToCartButton) {
        addToCartButton.click();
        console.log("addToCartButton.click()");
      }

      setTimeout(() => {
        lock = false;
      }, 1000);
    });

    // Compute initial totals if fields are already populated.
    this._inputChange();
  }

  // Saves the current inputs as a wall card and clears the inputs.
  _addWall() {
    const inputWidth = this.querySelector("#calc-width");
    const inputHeight = this.querySelector("#calc-height");

    const w = parseFloat(inputWidth.value);
    const h = parseFloat(inputHeight.value);
    if (!w || !h || isNaN(w * h)) return;

    this._walls.push({ width: w, height: h });
    inputWidth.value = "";
    inputHeight.value = "";

    this._renderWalls();
    this._inputChange();
  }

  _renderWalls() {
    const list = this.querySelector(".product-footage__walls");
    if (!list) return;

    list.innerHTML = "";

    this._walls.forEach((wall, i) => {
      const li = document.createElement("li");
      li.className = "product-footage__wall-card";
      li.innerHTML = `
        <span class="product-footage__wall-label">Wall ${i + 1}</span>
        <span class="product-footage__wall-dims">${wall.width} &times; ${wall.height} ft</span>
        <span class="product-footage__wall-sqft">${wall.width * wall.height} sq ft</span>
        <button type="button" class="product-footage__wall-remove" aria-label="Remove wall ${i + 1}">&times;</button>
      `;
      li.querySelector(".product-footage__wall-remove").addEventListener("click", () => {
        this._walls.splice(i, 1);
        this._renderWalls();
        this._inputChange();
      });
      list.appendChild(li);
    });
  }

  _inputChange() {
    const inputHeight = this.querySelector("#calc-height");
    const inputWidth = this.querySelector("#calc-width");
    const totalElem = this.querySelector("#calc-total");
    const calcPrice = this.querySelector("#calc-price");
    const calcAdd = this.querySelector(".trigger-calc-add");
    const add15 = this.querySelector("#add15");
    const addWallBtn = this.querySelector(".calc-add-wall");

    const currentW = parseFloat(inputWidth.value);
    const currentH = parseFloat(inputHeight.value);
    const currentValid = currentW > 0 && currentH > 0 && !isNaN(currentW * currentH);

    // Enable "Add a wall" only when both current inputs are valid.
    if (addWallBtn) {
      addWallBtn.disabled = !currentValid;
    }

    // All walls to calculate against: saved + current pending input (if valid).
    const allWalls = [
      ...this._walls,
      ...(currentValid ? [{ width: currentW, height: currentH }] : []),
    ];

    const totalSqFt = allWalls.reduce((sum, w) => sum + w.width * w.height, 0);
    totalElem.textContent = totalSqFt + " sq ft.";

    if (allWalls.length === 0) {
      setCalcAddEnabled(calcAdd, false);
      return;
    }

    let selected;
    const options = this.querySelectorAll(".product-footage__panels-option");

    options.forEach((opt) => {
      const optWidth = parseFloat(opt.getAttribute("data-width"));
      const optHeight = parseFloat(opt.getAttribute("data-height"));
      const optDiviser = parseInt(opt.getAttribute("data-diviser") || "1");
      const optPack = parseInt(opt.getAttribute("data-pack-size") || "1");
      const verbage = opt.getAttribute("data-verbage") || "panels";

      // Sum panel counts across every wall.
      let total = allWalls.reduce((sum, wall) => {
        const widthCount = Math.ceil(wall.width / Math.floor(optWidth / optDiviser));
        const heightCount = Math.ceil(wall.height / Math.floor(optHeight / optDiviser));
        return sum + Math.ceil((widthCount * heightCount) / optPack);
      }, 0);

      if (add15.checked) {
        total = Math.ceil(total * 1.15);
      }

      opt.setAttribute("data-count", total);
      opt.querySelector(".calc-update").textContent = total + " " + verbage;

      if (opt.classList.contains("is-selected")) {
        selected = opt;
      }
    });

    if (selected) {
      const variantPicker = this._getFromPdp("variant-selects, variant-radios");
      const currentVariantPrice = variantPicker?.currentVariant?.price;
      if (!currentVariantPrice) return;
      const curPrice = currentVariantPrice / 100;

      calcPrice.textContent =
        "$" +
        (curPrice * parseInt(selected.getAttribute("data-count"))).toLocaleString("en-US", {
          minimumFractionDigits: 0,
        });
      calcPrice.setAttribute("data-usd", curPrice * parseInt(selected.getAttribute("data-count")));

      setCalcAddEnabled(calcAdd, true);
      calcAdd.setAttribute("data-quantity", selected.getAttribute("data-count"));
      const quantityInput =
        this._getFromPdp(".product-form__quantity .quantity__input") ||
        this._getFromPdp("input[name='quantity']");
      if (quantityInput) quantityInput.value = selected.getAttribute("data-count");
    } else {
      setCalcAddEnabled(calcAdd, false);
    }
  }
}

if (!customElements.get("footage-calculator")) {
  customElements.define("footage-calculator", FootageCalculator);
}
